import { Link } from '@tanstack/react-router';
import { Copy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, CardContent, Dialog, DialogContent, Field, FormError, Input, PageHeader, Select, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Textarea, formatDate, useToast, type BadgeProps } from '@marketingbee/ui';
import { FeatureGate, Meter, QueryView, Section } from '../components/common';
import { BrandPicker, copyText, useActiveBrand, useCanManageContent, type BrandRow } from '../components/marketing';
import { errorMessage, trpc, type RouterOutputs } from '../lib/trpc';

type Subscriber = RouterOutputs['email']['subscribers']['subscribers'][number];
type Consent = Subscriber['consent'];
type ConsentKind = 'express' | 'purchase' | 'inquiry' | 'b2b' | 'withdrawn';

const KIND_OPTIONS: { value: ConsentKind; label: string; help: string }[] = [
  { value: 'express', label: 'Express consent', help: 'They clearly agreed to receive marketing email (a form, a signed sheet, a recorded call). Lasts until they withdraw.' },
  { value: 'purchase', label: 'Implied: purchase', help: 'They bought from you. Implied consent lasts 2 years from the purchase.' },
  { value: 'inquiry', label: 'Implied: inquiry', help: 'They asked you about your products or services. Implied consent lasts 6 months from the inquiry.' },
  { value: 'b2b', label: 'Implied: published business address', help: 'They publish their business email without saying "no marketing", and your message is relevant to their role.' },
  { value: 'withdrawn', label: 'Withdrawn', help: 'They asked to stop. They will not be emailed again.' },
];

export function consentLabel(c: Consent): { label: string; variant: BadgeProps['variant']; detail?: string } {
  if (c.canEmail) {
    const until = c.expiresAt ? `until ${formatDate(c.expiresAt)}` : undefined;
    if (c.basis === 'express') return { label: 'Express', variant: 'success' };
    if (c.basis === 'implied_purchase') return { label: 'Implied (purchase)', variant: 'info', detail: until };
    if (c.basis === 'implied_inquiry') return { label: 'Implied (inquiry)', variant: 'info', detail: until };
    return { label: 'Implied (b2b)', variant: 'info', detail: 'relevant to their role only' };
  }
  switch (c.reason) {
    case 'pending_confirmation':
      return { label: 'Pending confirmation', variant: 'warning' };
    case 'withdrawn':
      return { label: 'Withdrawn', variant: 'destructive' };
    case 'expired':
      return { label: 'Expired', variant: 'outline', detail: 'implied consent ran out' };
    case 'bounced':
      return { label: 'Bounced', variant: 'destructive' };
    case 'complained':
      return { label: 'Marked as spam', variant: 'destructive' };
    default:
      return { label: 'None', variant: 'outline' };
  }
}

export function EmailPage() {
  const { brands, brand, setBrandId } = useActiveBrand();
  return (
    <div className="space-y-8">
      <PageHeader title="Email" description="Your subscribers and their consent. Marketing email only goes to people who can legally receive it." actions={<BrandPicker brands={brands} value={brand?.id} onChange={setBrandId} />} />
      <FeatureGate feature="email_marketing">
        <Subscribers />
        {brand ? <SignupForm brand={brand} /> : null}
      </FeatureGate>
    </div>
  );
}

function Subscribers() {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);
  const list = trpc.email.subscribers.useQuery(debounced ? { search: debounced } : undefined);
  const canManage = useCanManageContent();
  const [recordFor, setRecordFor] = useState<Subscriber | null>(null);
  return (
    <Section title="Subscribers" description="Everyone with an email address. Consent is folded from the ledger: every sign-up, confirmation and withdrawal, with the wording shown.">
      <QueryView query={list} label="Loading subscribers">
        {(data) => (
          <div className="space-y-4">
            <Card>
              <CardContent className="space-y-2 py-5">
                <Meter label="Contacts you can email" used={data.emailable} limit={data.limit} unit="" />
                {data.overLimit ? <Alert variant="warning">You have more emailable contacts than your plan allows. Sends stop at the limit; upgrade or remove contacts.</Alert> : null}
              </CardContent>
            </Card>
            <Input className="max-w-sm" placeholder="Search name or email" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search subscribers" />
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Consent</TableHead>
                    <TableHead>Added</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.subscribers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-muted-foreground">{debounced ? 'No one matches.' : 'No contacts with an email address yet. Add the sign-up form to your site.'}</TableCell>
                    </TableRow>
                  ) : null}
                  {data.subscribers.map((s) => {
                    const c = consentLabel(s.consent);
                    return (
                      <TableRow key={s.id}>
                        <TableCell className="font-medium">
                          <Link to="/crm/contacts/$contactId" params={{ contactId: s.id }} className="hover:underline">{s.displayName}</Link>
                        </TableCell>
                        <TableCell className="text-[13px]">{s.email}</TableCell>
                        <TableCell>
                          <Badge variant={c.variant}>{c.label}</Badge>
                          {c.detail ? <span className="ml-2 text-[12px] text-muted-foreground">{c.detail}</span> : null}
                        </TableCell>
                        <TableCell className="text-[13px]">{formatDate(s.createdAt)}</TableCell>
                        <TableCell className="text-right">
                          {canManage ? (
                            <Button size="sm" variant="ghost" onClick={() => setRecordFor(s)}>
                              Record consent
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          </div>
        )}
      </QueryView>
      <RecordConsentDialog contact={recordFor} onClose={() => setRecordFor(null)} />
    </Section>
  );
}

function RecordConsentDialog({ contact, onClose }: { contact: Subscriber | null; onClose: () => void }) {
  const record = trpc.email.recordConsent.useMutation();
  const utils = trpc.useUtils();
  const { success } = useToast();
  const [kind, setKind] = useState<ConsentKind>('express');
  const [wording, setWording] = useState('');
  const [date, setDate] = useState('');
  useEffect(() => {
    if (contact) {
      setKind('express');
      setWording('');
      setDate('');
      record.reset();
    }
  }, [contact?.id]);
  const help = KIND_OPTIONS.find((k) => k.value === kind)?.help;
  return (
    <Dialog open={!!contact} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        title={`Record consent: ${contact?.displayName ?? ''}`}
        description="Under Canada's anti-spam law (CASL) the sender must prove consent. Write down how and when it was given, and what the person was told."
        footer={
          <>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button
              disabled={wording.trim().length < 5}
              loading={record.isPending}
              onClick={() =>
                contact &&
                record.mutate(
                  { contactId: contact.id, kind, wording, occurredAt: date ? new Date(`${date}T12:00:00`) : undefined },
                  {
                    onSuccess: async () => {
                      success('Consent recorded', 'It is kept in the contact’s consent ledger.');
                      onClose();
                      await Promise.all([utils.email.subscribers.invalidate(), utils.crm.contacts.get.invalidate({ id: contact.id })]);
                    },
                  },
                )
              }
            >
              Record
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {record.error ? <FormError message={errorMessage(record.error)} /> : null}
          <Field label="Kind" description={help}>
            <Select value={kind} onChange={(e) => setKind(e.target.value as ConsentKind)} options={KIND_OPTIONS.map((k) => ({ value: k.value, label: k.label }))} />
          </Field>
          <Field label="What happened, in your words" description="For example: 'Ticked the newsletter box on the paper form at the Toronto market, 12 Oct.' At least 5 characters.">
            <Textarea rows={3} value={wording} maxLength={1000} onChange={(e) => setWording(e.target.value)} />
          </Field>
          <Field label="When (optional)" description="Defaults to now. Implied consent is counted from this date.">
            <Input type="date" value={date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setDate(e.target.value)} className="max-w-48" />
          </Field>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SignupForm({ brand }: { brand: BrandRow }) {
  const form = trpc.email.form.useQuery({ brandId: brand.id });
  const { success, error } = useToast();
  return (
    <Section title="Sign-up form" description="Paste this into your website. It collects the exact consent wording, and new subscribers get a confirmation email (double opt-in): nobody is emailed until they click it.">
      <QueryView query={form}>
        {(f) => {
          const snippet = embedSnippet(f.action, f.wording, brand.name);
          return (
            <Card>
              <CardContent className="space-y-3 py-5">
                {!f.ready ? (
                  <Alert variant="warning" title="Add a mailing address first">
                    The form does not accept sign-ups until the brand has a mailing address. <Link className="font-semibold underline" to="/brand">Open Brand</Link>
                  </Alert>
                ) : null}
                <p className="text-[13px] text-muted-foreground">Consent wording shown next to the checkbox:</p>
                <blockquote className="rounded-lg bg-muted px-4 py-3 text-[14px]">{f.wording}</blockquote>
                <pre className="max-h-80 overflow-auto rounded-lg bg-foreground/[0.04] p-4 font-mono text-[12px] leading-relaxed"><code>{snippet}</code></pre>
                <Button size="sm" variant="outline" onClick={() => void copyText(snippet).then((ok) => (ok ? success('Snippet copied') : error('Could not copy', 'Select the text and copy it.')))}>
                  <Copy /> Copy snippet
                </Button>
              </CardContent>
            </Card>
          );
        }}
      </QueryView>
    </Section>
  );
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function embedSnippet(action: string, wording: string, brandName: string): string {
  return `<form method="post" action="${escapeHtml(action)}">
  <label>Email <input type="email" name="email" required></label>
  <label>First name <input type="text" name="firstName"></label>
  <label>
    <input type="checkbox" name="consent" value="true" required>
    ${escapeHtml(wording)}
  </label>
  <input type="hidden" name="source" value="website">
  <button type="submit">Subscribe to ${escapeHtml(brandName)}</button>
</form>`;
}
